import {CarouselPagination, Text, useTheme} from '@primer/react-brand/esm'
import {clsx} from 'clsx'

import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {PlayButton} from '../../../../../../brand/components/PlayButton/PlayButton'

import {useHeroPagination} from './use-hero-pagination'
import type {HeroPaginationProps, HeroPaginationTab} from './types'

import Styles from './HeroPagination.module.css'

export type {HeroPaginationProps} from './types'

export const defaultProps: Partial<HeroPaginationProps> = {}

export const HeroPagination = (props: HeroPaginationProps) => {
  const initializedProps: HeroPaginationProps = {...defaultProps, ...props}
  const {currentChapterIndex, items, onChange, a11y, video} = initializedProps

  const {colorMode} = useTheme()
  const prefersReducedMotion = usePrefersReducedMotion()

  const {
    hasScrolled,
    firstItemRef,
    playButtonAction,
    viewportRef,
    selectedButtonRef,
    progressRef,
    selectChapter,
    handlePlaybackClick,
  } = useHeroPagination({
    currentChapterIndex,
    items,
    onChange,
    video,
    prefersReducedMotion,
  })

  const getItemTab = (item: HeroPaginationTab, index: number) => {
    const isSelected = index === currentChapterIndex

    return (
      <li className={Styles['HeroPagination__chapters_item']} key={index} ref={index === 0 ? firstItemRef : undefined}>
        <button
          type="button"
          className={Styles['HeroPagination__chapters_button']}
          aria-current={isSelected ? 'step' : undefined}
          ref={isSelected ? selectedButtonRef : undefined}
          onClick={() => selectChapter(index)}
        >
          {isSelected && (
            <span className={Styles['HeroPagination__chapters_progress']} ref={progressRef} aria-hidden="true" />
          )}
          <Text
            size="200"
            variant={isSelected ? 'default' : 'muted'}
            className={Styles['HeroPagination__chapters_label']}
          >
            {item.label}
          </Text>
        </button>
      </li>
    )
  }

  const className = clsx(Styles['HeroPagination'], colorMode === 'dark' && Styles['HeroPagination--dark'])

  if (items.length === 0) return <div className={className} />

  return (
    <div className={className}>
      <div className={Styles['HeroPagination__chapters']} data-scrolled={hasScrolled}>
        <div className={Styles['HeroPagination__chapters_viewport']} ref={viewportRef}>
          <ul className={Styles['HeroPagination__chapters_list']}>{items.map(getItemTab)}</ul>
        </div>
      </div>
      <div className={Styles['HeroPagination__controls']}>
        <div className={Styles['HeroPagination__controls_pagination']}>
          <CarouselPagination
            aria-label={a11y.pagination.title}
            itemCount={items.length}
            activeIndex={currentChapterIndex}
            loop
            onChange={selectChapter}
            labels={{
              previous: a11y.pagination.previous,
              next: a11y.pagination.next,
            }}
            itemAttributesBuilder={index => ({'aria-label': items[index]?.label})}
          />
        </div>
        {!prefersReducedMotion && (
          <PlayButton
            action={playButtonAction}
            onClick={handlePlaybackClick}
            label={a11y.playButton[playButtonAction]}
            className={Styles['HeroPagination__playback']}
          />
        )}
      </div>
    </div>
  )
}
